import { useState, useMemo } from 'react';
import type { StudioListener } from '@/mocks/adminStudio';
import { studioListeners } from '@/mocks/adminStudio';
import { SwalAlert } from '@/hooks/useSweetAlert';

function getTimeListening(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

const vipColors: Record<string, string> = {
  gold: 'bg-amber-100 text-amber-700',
  platinum: 'bg-violet-100 text-violet-700',
  diamond: 'bg-rose-100 text-rose-700',
};

const vipLabels: Record<string, string> = {
  gold: 'GOLD',
  platinum: 'PLAT',
  diamond: 'DIAM',
};

export default function ListenersPanel() {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return studioListeners;
    const q = search.toLowerCase();
    return studioListeners.filter((l) => l.name.toLowerCase().includes(q) || l.location.toLowerCase().includes(q));
  }, [search]);

  const onlineCount = studioListeners.filter((l) => l.status === 'online').length;
  const vipCount = studioListeners.filter((l) => l.isVip).length;

  return (
    <div className="h-full flex flex-col bg-background-50">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-background-200">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center">
              <i className="ri-user-voice-line text-background-50 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground-950">Oyentes ahora</h3>
              <p className="text-[10px] text-foreground-400">{studioListeners.length} sintonizando · {vipCount} VIP</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              {onlineCount}
            </span>
          </div>
        </div>
        {/* Search */}
        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar oyente..."
            className="w-full pl-7 pr-7 py-1.5 rounded-lg bg-background-100 text-foreground-800 text-[11px] border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none placeholder:text-foreground-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400">
              <i className="ri-close-line text-background-50 text-[7px]"></i>
            </button>
          )}
        </div>
      </div>

      {/* Listener list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center px-4">
            <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mb-2">
              <i className="ri-user-search-line text-foreground-300 text-lg"></i>
            </div>
            <p className="text-xs font-bold text-foreground-400">Sin resultados</p>
            <p className="text-[10px] text-foreground-400">Ningún oyente coincide con "{search}"</p>
          </div>
        ) : (
          filtered.map((listener) => (
            <ListenerRow key={listener.id} listener={listener} />
          ))
        )}
      </div>

      {/* Footer stats */}
      <div className="shrink-0 px-4 py-2.5 border-t border-background-200 bg-background-50 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
          <i className="ri-signal-wifi-line text-xs"></i>
          <span>94% retención</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
          <i className="ri-time-line text-xs"></i>
          <span>Sesión media 18.5m</span>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
          <i className="ri-message-2-line text-xs"></i>
          <span>78 hoy</span>
        </div>
      </div>
    </div>
  );
}

function ListenerRow({ listener }: { listener: StudioListener }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 border-b border-background-100 hover:bg-background-50 cursor-pointer transition-colors ${listener.status === 'idle' ? 'opacity-60' : ''}`}>
      <div className="relative shrink-0">
        <div className="w-8 h-8 rounded-full bg-background-200 overflow-hidden">
          <img src={listener.avatar} alt={listener.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background-50 ${listener.status === 'online' ? 'bg-emerald-500' : 'bg-amber-400'}`}></span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-foreground-800 truncate">{listener.name}</span>
          {listener.isVip && listener.vipTier && (
            <span className={`px-1 py-0 rounded text-[8px] font-bold ${vipColors[listener.vipTier]}`}>
              {vipLabels[listener.vipTier]}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-foreground-400 flex items-center gap-0.5">
            <i className="ri-map-pin-line text-[8px]"></i>{listener.location}
          </span>
          <span className="text-[9px] text-foreground-400">{getTimeListening(listener.listeningSince)}</span>
          {listener.messagesToday > 0 && (
            <span className="text-[9px] text-foreground-400">{listener.messagesToday} msgs</span>
          )}
        </div>
      </div>
      <button onClick={() => SwalAlert.toast(`Perfil de ${listener.name} próximamente`, 'info')} className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center cursor-pointer hover:bg-secondary-100 hover:text-secondary-600 transition-colors shrink-0" title="Ver perfil">
        <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
      </button>
    </div>
  );
}